import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { initialValues as defaultValues, lease_modes_allowed } from '../config';
import { getLeaseStatus, populatePropertyDetails, populateFormWithLeaseData, setLeaseKind } from '../utils';

/** `?kind=renewal|new` from the Mastersheet's Manage Lease prompt → "RENEWAL" | "NEW" | null. */
const leaseKindFrom = (search) => {
  const kind = new URLSearchParams(search).get('kind');
  return kind === 'renewal' ? 'RENEWAL' : kind === 'new' ? 'NEW' : null;
};

const withLeaseKind = (values, kind) =>
  kind ? { ...values, version_info: { ...values?.version_info, lease_kind: kind, is_renewal: kind === 'RENEWAL' } } : values;

/**
 * Hook to fetch and initialize lease form values based on URL parameters and lease status.
 *
 * @param {Object} options - Configuration options.
 * @param {Function} [options.setCurrentStep] - Function to set the current step index.
 * @param {Function} [options.setError] - Function to set an error message.
 * @returns {Object} - Initial values, loading state, mode, and lease ID.
 */
export const useLeaseInitialValues = ({ currentStep, setCurrentStep, setError } = {}) => {
  const { mode = '', property_id, unit_no, building_name } = useParams();
  const initialMode = lease_modes_allowed[mode] || null;
  const leaseKind = leaseKindFrom(useLocation().search);
  const reduxValues = useSelector((state) => state.leaseForm);
  const [finalValues, setFinalValues] = useState(null);
  const [leaseId, setLeaseId] = useState(null);
  const [loading, setLoading] = useState(true);

  const hasInitializedRef = useRef(false);

  /**
   * Checks if session storage values match current URL parameters.
   *
   * @returns {boolean} - True if session storage params match current params.
   */
  const doSessionParamsMatch = () => {
    try {
      const sessionParams = sessionStorage.getItem('lease_values');
      if (!sessionParams) return false;
      const { property_details: { unit_no: storedUnitNo, building_name: storedBuildingName } } = JSON.parse(sessionParams);
      return (
        storedUnitNo === unit_no &&
        storedBuildingName === building_name
      );
    } catch (error) {
      return false;
    }
  };

  /**
   * Generates mode-based initial values for the lease form.
   *
   * @param {Object} baseValues - Base initial values.
   * @returns {Object} - Mode-specific initial values.
   */
  const getModeBasedInitialValues = (baseValues) => {
    const now = new Date();
    switch (initialMode) {
      case 'new_lease':
        return {
          ...baseValues,
          lease_status: { state: 'NEW_LEASE', effective_date: now, end_reason: '', notes: '' },
          version_info: {
            version_number: 1,
            previous_lease_id: null,
            is_renewal: false,
            is_amendment: false,
            original_lease_id: null,
          },
        };
      case 'release_request':
        return {
          ...baseValues,
          lease_status: { state: 'RELEASE_REQUEST', effective_date: now, end_reason: '', notes: '' },
          version_info: {
            version_number: 1,
            previous_lease_id: null,
            is_renewal: true,
            is_amendment: false,
            original_lease_id: null,
          },
        };
      case 'scheduled':
        return {
          ...baseValues,
          lease_status: { state: 'SCHEDULED', effective_date: now, end_reason: '', notes: '' },
          version_info: {
            version_number: 1,
            previous_lease_id: null,
            is_renewal: false,
            is_amendment: false,
            original_lease_id: null,
          },
        };
      default:
        return baseValues;
    }
  };

  /**
   * Maps lease step to step index.
   *
   * @param {string} step - Step identifier (e.g., 'step1').
   * @returns {number} - Corresponding step index.
   */
  const getStepIndex = (step) => {
    const stepMap = { step1: 0, step2: 1, step3: 2, step4: 3, step5: 4 };
    return stepMap[step] ?? 0;
  };

  /**
   * Fetches and processes lease initial values.
   */
  const fetchInitialValues = async () => {
    try {
      const modeValue = lease_modes_allowed[initialMode] || '';
      const modeBasedValues = getModeBasedInitialValues(defaultValues);

      const response = await getLeaseStatus({ property_id, unit_no, building_name, mode: modeValue });

      let values = modeBasedValues;

      if (response?.step === 'step1') {
        const propertyData = await populatePropertyDetails(property_id, building_name, unit_no, true);
        if (propertyData) {
          values = {
            ...values,
            property_details: propertyData.property_details,
            manager: propertyData.manager,
          };
        }
      }

      if (response?.lease) {
        const { leaseData, leaseId: responseLeaseId } = populateFormWithLeaseData({ lease: response.lease });
        values = { ...values, ...leaseData };
        if (responseLeaseId) {
          setLeaseId(responseLeaseId);
          // A draft already exists: step 1 will not run again, so save the answer now.
          if (leaseKind && leaseData?.version_info?.lease_kind !== leaseKind) {
            setLeaseKind(responseLeaseId, leaseKind).catch((err) => console.warn('Could not save lease kind:', err));
          }
        }
      } else if (reduxValues !== defaultValues && Object.keys(reduxValues).length > 0) {
        values = reduxValues;
      }
      setFinalValues(withLeaseKind(values, leaseKind));
      if (response?.step && response?.success) {
        setCurrentStep?.(getStepIndex(response.step));
      } else {
        setCurrentStep?.(0);
      }

      hasInitializedRef.current = true;
    } catch (err) {
      setError?.('Failed to fetch lease status. Please try again or contact support.');
      setCurrentStep?.(0);
      setFinalValues(withLeaseKind(reduxValues && Object.keys(reduxValues).length > 0 ? reduxValues : getModeBasedInitialValues(defaultValues), leaseKind));
      hasInitializedRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    hasInitializedRef.current = false;
    setLoading(true);

    if (!doSessionParamsMatch()) {
      try {
        sessionStorage.removeItem('lease_values');
      } catch (error) {
        console.warn('Failed to clear session storage:', error);
      }
    }

    fetchInitialValues();
  }, [property_id, unit_no, building_name, initialMode, currentStep, leaseKind]);

  useEffect(() => {
    if (hasInitializedRef.current && reduxValues && Object.keys(reduxValues).length > 0 && reduxValues !== defaultValues) {
      setFinalValues(reduxValues);
    }
  }, [reduxValues]);

  return { initialValues: finalValues, loading, initialMode, leaseId };
};
